/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Image,
  Alert,
  FlatList,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
  MaterialIcons,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id')
                      || await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId), company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const ProjectAssignmentScreen = ({ navigation, route }) => {
  const { subcontractor } = route.params || {};
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [assignmentDetails, setAssignmentDetails] = useState({
    startDate: '',
    endDate: '',
    workingHours: '08:00 - 17:00',
    specialTasks: '',
    budget: '',
    priority: 'Medium',
    notes: '',
  });

  // Mock projects data
  const [projects] = useState([
    {
      id: 'p1',
      name: 'Residential Complex B',
      location: 'Manchester, UK',
      status: 'Planning',
      startDate: '15/01/2024',
      endDate: '30/06/2024',
      budget: '£250,000',
      manager: 'John Smith',
      description: 'New residential complex with 50 units',
      requiredSpecialties: ['Concrete Work', 'Foundation', 'Structural'],
      urgency: 'High',
    },
    {
      id: 'p2',
      name: 'Office Building Renovation',
      location: 'London, UK',
      status: 'In Progress',
      startDate: '01/12/2023',
      endDate: '28/03/2024',
      budget: '£180,000',
      manager: 'Sarah Johnson',
      description: 'Complete renovation of 5-story office building',
      requiredSpecialties: ['Electrical', 'Plumbing', 'Interior Finishing'],
      urgency: 'Medium',
    },
    {
      id: 'p3',
      name: 'Shopping Center Extension',
      location: 'Birmingham, UK',
      status: 'Planning',
      startDate: '01/03/2024',
      endDate: '15/08/2024',
      budget: '£400,000',
      manager: 'Mike Wilson',
      description: 'Extension of existing shopping center',
      requiredSpecialties: ['Concrete Work', 'Structural', 'Roofing'],
      urgency: 'Low',
    },
    {
      id: 'p4',
      name: 'Hospital Wing Construction',
      location: 'Leeds, UK',
      status: 'Planning',
      startDate: '20/02/2024',
      endDate: '30/09/2024',
      budget: '£600,000',
      manager: 'Emma Davis',
      description: 'New wing for Leeds General Hospital',
      requiredSpecialties: ['Foundation', 'Structural', 'Electrical', 'Plumbing'],
      urgency: 'High',
    },
  ]);

  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  useEffect(() => {
    if (subcontractor) {
      // Filter projects that match subcontractor's specializations
      const matchingProjects = projects.filter(project =>
        project.requiredSpecialties.some(specialty =>
          subcontractor.specialization.includes(specialty)
        )
      );
      setFilteredProjects(matchingProjects);
    }
  }, [subcontractor, projects]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = projects.filter(project =>
        String(project.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(project.location ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(project.manager ?? '').toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredProjects(filtered);
    } else if (subcontractor) {
      const matchingProjects = projects.filter(project =>
        project.requiredSpecialties.some(specialty =>
          subcontractor.specialization.includes(specialty)
        )
      );
      setFilteredProjects(matchingProjects);
    } else {
      setFilteredProjects(projects);
    }
  }, [searchQuery, subcontractor, projects]);

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'High':
        return '#ff4757';
      case 'Medium':
        return '#fdb913';
      case 'Low':
        return '#19abb5';
      default:
        return '#999';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Planning':
        return '#fdb913';
      case 'In Progress':
        return '#19abb5';
      case 'Completed':
        return '#2ed573';
      default:
        return '#999';
    }
  };

  const handleAssignment = () => {
    if (!selectedProject) {
      Alert.alert('Error', 'Please select a project');
      return;
    }

    if (!assignmentDetails.startDate || !assignmentDetails.endDate) {
      Alert.alert('Error', 'Please provide start and end dates');
      return;
    }

    Alert.alert(
      'Confirm Assignment',
      `Assign ${subcontractor?.name} to ${selectedProject.name}?`,
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Confirm',
          onPress: () => {
            Alert.alert(
              'Success',
              'Subcontractor assigned successfully!',
              [
                {
                  text: 'OK',
                  onPress: () => navigation.goBack(),
                },
              ]
            );
          },
        },
      ]
    );
  };

  const renderProjectCard = ({ item }) => {
    const isSelected = selectedProject?.id === item.id;
    const hasMatchingSpecialties = subcontractor?.specialization.some(specialty =>
      item.requiredSpecialties.includes(specialty)
    );

    return (
      <TouchableOpacity
        style={[
          styles.projectCard,
          isSelected && styles.selectedProjectCard,
          !hasMatchingSpecialties && styles.nonMatchingProjectCard,
        ]}
        onPress={() => setSelectedProject(item)}
      >
        <View style={styles.projectHeader}>
          <View style={styles.projectTitleContainer}>
            <Text style={styles.projectName}>{item.name}</Text>
            <View style={styles.projectBadges}>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
                <Text style={styles.statusText}>{item.status}</Text>
              </View>
              <View style={[styles.urgencyBadge, { backgroundColor: getUrgencyColor(item.urgency) }]}>
                <Text style={styles.urgencyText}>{item.urgency}</Text>
              </View>
            </View>
          </View>
          
          {isSelected && (
            <Ionicons name="checkmark-circle" size={24} color="#19abb5" />
          )}
        </View>

        <Text style={styles.projectDescription}>{item.description}</Text>

        <View style={styles.projectDetails}>
          <View style={styles.projectDetailItem}>
            <Ionicons name="location-outline" size={14} color="#666" />
            <Text style={styles.projectDetailText}>{item.location}</Text>
          </View>
          
          <View style={styles.projectDetailItem}>
            <Ionicons name="person-outline" size={14} color="#666" />
            <Text style={styles.projectDetailText}>Manager: {item.manager}</Text>
          </View>
          
          <View style={styles.projectDetailItem}>
            <MaterialCommunityIcons name="cash" size={14} color="#666" />
            <Text style={styles.projectDetailText}>Budget: {item.budget}</Text>
          </View>
          
          <View style={styles.projectDetailItem}>
            <Ionicons name="calendar-outline" size={14} color="#666" />
            <Text style={styles.projectDetailText}>
              {item.startDate} - {item.endDate}
            </Text>
          </View>
        </View>

        <View style={styles.specialtiesSection}>
          <Text style={styles.specialtiesLabel}>Required Specialties:</Text>
          <View style={styles.specialtiesTags}>
            {item.requiredSpecialties.map((specialty, index) => {
              const isMatching = subcontractor?.specialization.includes(specialty);
              return (
                <View
                  key={index}
                  style={[
                    styles.specialtyTag,
                    isMatching ? styles.matchingSpecialtyTag : styles.nonMatchingSpecialtyTag,
                  ]}
                >
                  <Text
                    style={[
                      styles.specialtyTagText,
                      isMatching ? styles.matchingSpecialtyText : styles.nonMatchingSpecialtyText,
                    ]}
                  >
                    {specialty}
                  </Text>
                  {isMatching && (
                    <Ionicons name="checkmark" size={12} color="#19abb5" style={styles.matchIcon} />
                  )}
                </View>
              );
            })}
          </View>
        </View>

        {!hasMatchingSpecialties && (
          <View style={styles.warningContainer}>
            <MaterialIcons name="warning" size={16} color="#ff4757" />
            <Text style={styles.warningText}>
              No matching specializations with this subcontractor
            </Text>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        
        {companyLogo ? (
          <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
        ) : (
          <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
        )}
        
        <Text style={styles.headerTitle}>Project Assignment</Text>
        
        <TouchableOpacity onPress={handleAssignment}>
          <Ionicons name="checkmark" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Subcontractor Info */}
      {subcontractor && (
        <View style={styles.subcontractorInfo}>
          <Text style={styles.subcontractorName}>{subcontractor.name}</Text>
          <Text style={styles.subcontractorSpecialties}>
            Specialties: {subcontractor.specialization.join(', ')}
          </Text>
        </View>
      )}

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchBar}>
          <Ionicons name="search" size={20} color="#999" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search projects..."
            placeholderTextColor="#999"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#999" />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      {/* Projects List */}
      <View style={styles.projectsSection}>
        <Text style={styles.sectionTitle}>
          Available Projects ({filteredProjects.length})
        </Text>
        
        <FlatList
          data={filteredProjects}
          renderItem={renderProjectCard}
          keyExtractor={item => item.id}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialIcons name="search-off" size={48} color="#ccc" />
              <Text style={styles.emptyText}>No projects found</Text>
              <Text style={styles.emptySubtext}>
                Try adjusting your search criteria
              </Text>
            </View>
          }
        />
      </View>

      {/* Assignment Details */}
      {selectedProject && (
        <View style={styles.assignmentDetails}>
          <Text style={styles.assignmentTitle}>Assignment Details</Text>
          
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.assignmentForm}>
              <View style={styles.formRow}>
                <View style={styles.formField}>
                  <Text style={styles.fieldLabel}>Start Date</Text>
                  <TextInput
                    style={styles.fieldInput}
                    value={assignmentDetails.startDate}
                    onChangeText={(text) => 
                      setAssignmentDetails(prev => ({ ...prev, startDate: text }))
                    }
                    placeholder="DD/MM/YYYY"
                    placeholderTextColor="#999"
                  />
                </View>

                <View style={styles.formField}>
                  <Text style={styles.fieldLabel}>End Date</Text>
                  <TextInput
                    style={styles.fieldInput}
                    value={assignmentDetails.endDate}
                    onChangeText={(text) => 
                      setAssignmentDetails(prev => ({ ...prev, endDate: text }))
                    }
                    placeholder="DD/MM/YYYY"
                    placeholderTextColor="#999"
                  />
                </View>

                <View style={styles.formField}>
                  <Text style={styles.fieldLabel}>Working Hours</Text>
                  <TextInput
                    style={styles.fieldInput}
                    value={assignmentDetails.workingHours}
                    onChangeText={(text) => 
                      setAssignmentDetails(prev => ({ ...prev, workingHours: text }))
                    }
                    placeholder="08:00 - 17:00"
                    placeholderTextColor="#999"
                  />
                </View>

                <View style={styles.formField}>
                  <Text style={styles.fieldLabel}>Budget</Text>
                  <TextInput
                    style={styles.fieldInput}
                    value={assignmentDetails.budget}
                    onChangeText={(text) => 
                      setAssignmentDetails(prev => ({ ...prev, budget: text }))
                    }
                    placeholder="£0"
                    placeholderTextColor="#999"
                  />
                </View>
              </View>
            </View>
          </ScrollView>

          <TouchableOpacity style={styles.assignButton} onPress={handleAssignment}>
            <MaterialCommunityIcons name="clipboard-text" size={20} color="#fff" />
            <Text style={styles.assignButtonText}>Assign to Project</Text>
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  subcontractorInfo: {
    backgroundColor: '#fff',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  subcontractorName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  subcontractorSpecialties: {
    fontSize: 14,
    color: '#666',
  },
  searchContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f7fa',
    borderRadius: 8,
    paddingHorizontal: 10,
  },
  searchIcon: {
    marginRight: 10,
  },
  searchInput: {
    flex: 1,
    height: 40,
    color: '#333',
  },
  projectsSection: {
    flex: 1,
    backgroundColor: '#f5f7fa',
    padding: 15,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  projectCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectedProjectCard: {
    borderColor: '#19abb5',
    backgroundColor: '#f0fffe',
  },
  nonMatchingProjectCard: {
    opacity: 0.7,
  },
  projectHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  projectTitleContainer: {
    flex: 1,
  },
  projectName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  projectBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  urgencyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  urgencyText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  projectDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
    lineHeight: 20,
  },
  projectDetails: {
    marginBottom: 12,
  },
  projectDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  projectDetailText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 6,
  },
  specialtiesSection: {
    marginBottom: 10,
  },
  specialtiesLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  specialtiesTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  specialtyTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
    marginBottom: 6,
    flexDirection: 'row',
    alignItems: 'center',
  },
  matchingSpecialtyTag: {
    backgroundColor: '#e0f7f7',
    borderWidth: 1,
    borderColor: '#19abb5',
  },
  nonMatchingSpecialtyTag: {
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  specialtyTagText: {
    fontSize: 10,
    fontWeight: '500',
  },
  matchingSpecialtyText: {
    color: '#19abb5',
  },
  nonMatchingSpecialtyText: {
    color: '#666',
  },
  matchIcon: {
    marginLeft: 4,
  },
  warningContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff5f5',
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ffebee',
  },
  warningText: {
    fontSize: 12,
    color: '#ff4757',
    marginLeft: 6,
    fontWeight: '500',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 30,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#999',
    marginTop: 10,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 5,
  },
  assignmentDetails: {
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    padding: 15,
  },
  assignmentTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  assignmentForm: {
    marginBottom: 15,
  },
  formRow: {
    flexDirection: 'row',
    gap: 10,
  },
  formField: {
    minWidth: 120,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    marginBottom: 5,
  },
  fieldInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 12,
    color: '#333',
  },
  assignButton: {
    backgroundColor: '#fdb913',
    paddingVertical: 12,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  assignButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
});

export default ProjectAssignmentScreen;